import React, { useState, useEffect } from 'react';
import { LineChart } from 'lucide-react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "./ui/alert-dialog";

const SummaryTab = ({ data, onUpdate, onTabChange, currentTab, requestedTab }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [initialData, setInitialData] = useState(null);

  // Vérification des changements non enregistrés
  useEffect(() => {
    if (data && !initialData) {
      setInitialData(JSON.stringify(data));
    }
  }, [data, initialData]);

  useEffect(() => {
    if (initialData) {
      const currentData = JSON.stringify(data);
      setHasUnsavedChanges(initialData !== currentData);
    }
  }, [data, initialData]);

  // Gestion du changement d'onglet
  useEffect(() => {
    if (requestedTab && requestedTab !== currentTab) {
      if (hasUnsavedChanges) {
        setShowExitDialog(true);
      } else {
        onTabChange(requestedTab);
      }
    }
  }, [requestedTab, currentTab, hasUnsavedChanges, onTabChange]);

  const handleSubmit = async () => {
    try {
      setIsUpdating(true);
      setError(null);
      await onUpdate(data);
      setInitialData(JSON.stringify(data));
      setHasUnsavedChanges(false);
      showSuccessNotification('Modifications enregistrées avec succès !');
    } catch (err) {
      setError('Erreur lors de la mise à jour. Veuillez réessayer.');
      console.error(err);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleConfirmTabChange = async (shouldSave) => {
    try {
      if (shouldSave) {
        await handleSubmit();
      }
      onTabChange(requestedTab); // Change l'onglet après la sauvegarde si nécessaire
    } catch (err) {
      setError('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setShowExitDialog(false);
    }
  };

  const showSuccessNotification = (message) => {
    const notification = document.createElement('div');
    notification.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg transform transition-transform duration-500 flex items-center';
    notification.innerHTML = `
      <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
      </svg>
      <span>${message}</span>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.transform = 'translateY(150%)';
      setTimeout(() => document.body.removeChild(notification), 500);
    }, 3000);
  };

  const handleSendEmail = async () => {
    try {
      setIsSendingEmail(true);
      setError(null);

      const emailData = {
        ecole: data?.Ecole,
        periode: data?.Période,
        classe: data?.Classe,
        instituteur: data?.Instit,
        email: data?.Mail,
        moniteur: data?.Moniteur,
        telephone: data?.telephone,
        eleves: {
          garcons: data?.Garçon,
          filles: data?.Fille,
          handicap: data?.Handicap,
          nonPedalant: data?.Nonpédalant,
          total: (data?.Garçon || 0) + (data?.Fille || 0)
        },
        planning: {
          dateCadrage: formatDate(data?.Datecadrage),
          bloc1: {
            seance1: formatDate(data?.B1S1),
            seance2: formatDate(data?.B1S2)
          },
          bloc2: {
            seance1: formatDate(data?.B2S1),
            seance2: formatDate(data?.B2S2)
          },
          bloc3: {
            seance1: formatDate(data?.B3S1),
            seance2: formatDate(data?.B3S2)
          },
          dateReport: formatDate(data?.Report)
        }
      };

      const response = await fetch('https://automate.naturavelo.net/webhook/eea5be92-efc2-4b59-b9f6-44aae9a3503b', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi du récapitulatif');
      }

      showSuccessNotification('Récapitulatif envoyé avec succès !');
    } catch (err) {
      setError('Erreur lors de l\'envoi du récapitulatif. Veuillez réessayer.');
      console.error(err);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non défini';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch {
      return 'Date invalide';
    }
  };

  // Composants d'affichage
  const InfoSection = ({ title, children }) => (
    <div className="bg-gray-50 p-4 rounded-lg mb-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
        {title}
      </h3>
      <div className="space-y-3">
        {children}
      </div>
    </div>
  );

  const InfoRow = ({ label, value }) => (
    <div className="grid grid-cols-2 gap-4">
      <dt className="text-sm font-medium text-gray-600">{label}</dt>
      <dd className="text-sm text-gray-900">{value || 'Non renseigné'}</dd>
    </div>
  );

  return (
    <>
      <div className="p-6 bg-white rounded-lg shadow">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <LineChart className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              Récapitulatif des informations
            </h2>
          </div>
          <span className="text-sm text-gray-500">
            École: {data?.Ecole} - Période: {data?.Période}
          </span>
        </div>

        {hasUnsavedChanges && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-600 rounded-lg flex items-center">
            <span className="mr-2">⚠️</span>
            <span>Modifications non enregistrées</span>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg">
            {error}
          </div>
        )}

        <div className="space-y-6">
          <InfoSection title="Informations générales">
            <InfoRow label="Classe" value={data?.Classe} />
            <InfoRow label="Instituteur/trice" value={data?.Instit} />
            <InfoRow label="Email" value={data?.Mail} />
            <InfoRow label="Téléphone" value={data?.telephone} />
          </InfoSection>

          <InfoSection title="Informations élèves">
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <InfoRow label="Garçons" value={data?.Garçon} />
              <InfoRow label="Filles" value={data?.Fille} />
              <InfoRow label="Élèves en situation de handicap" value={data?.Handicap} />
              <InfoRow label="Élèves non pédalants" value={data?.Nonpédalant} />
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200">
              <InfoRow 
                label="Total d'élèves" 
                value={(data?.Garçon || 0) + (data?.Fille || 0)} 
              />
            </div>
          </InfoSection>

          <InfoSection title="Planning des séances">
            <InfoRow label="Date de cadrage" value={formatDate(data?.Datecadrage)} />
            
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Bloc 1</p>
              <InfoRow label="Séance 1" value={formatDate(data?.B1S1)} />
              <InfoRow label="Séance 2" value={formatDate(data?.B1S2)} />
            </div>

            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Bloc 2</p>
              <InfoRow label="Séance 1" value={formatDate(data?.B2S1)} />
              <InfoRow label="Séance 2" value={formatDate(data?.B2S2)} />
            </div>

            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-2">Bloc 3</p>
              <InfoRow label="Séance 1" value={formatDate(data?.B3S1)} />
              <InfoRow label="Séance 2" value={formatDate(data?.B3S2)} />
            </div>

            <div className="mt-3 pt-3 border-t border-gray-200">
              <InfoRow label="Date de report" value={formatDate(data?.Report)} />
            </div>
          </InfoSection>

          <div className="flex justify-end pt-6 space-x-4">
            <button
              onClick={handleSendEmail}
              disabled={isSendingEmail}
              className={`
                px-6 py-2.5 rounded-lg text-white font-medium
                ${isSendingEmail 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'}
                focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
                transition-all duration-200
              `}
            >
              {isSendingEmail ? (
                <span className="flex items-center">
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Envoi en cours...
                </span>
              ) : (
                'Envoyer le récapitulatif par email'
              )}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isUpdating || !hasUnsavedChanges}
              className={`
                px-6 py-2.5 rounded-lg text-white font-medium
                ${(isUpdating || !hasUnsavedChanges)
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'}
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                transition-all duration-200
              `}
            >
              {isUpdating ? (
                <span className="flex items-center">
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Mise à jour en cours...
                </span>
              ) : (
                'Mettre à jour toutes les informations'
              )}
            </button>
          </div>
        </div>
      </div>

      <AlertDialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifications non enregistrées</AlertDialogTitle>
            <AlertDialogDescription>
              Vous avez des modifications non enregistrées. Voulez-vous les enregistrer avant de quitter cet onglet ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowExitDialog(false)}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleConfirmTabChange(false)}
            >
              Quitter sans enregistrer
            </AlertDialogAction>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleConfirmTabChange(true)}
            >
              Enregistrer et quitter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SummaryTab;